// Shared payment / conciliation helpers.
//
// Used by:
//  - /payments/new (CreatePayment.js) — multi-invoice payment from one client
//  - PaymentAllocationModal (per-reservation AddPayment follow-up)
//  - ConciliationDashboard quick-allocate
//  - Reservation.js saveConciliation (per-reservation quick conciliate)
//  - customerCredit.js apply-credit flow
//
// Public exports:
//   fetchClientOpenInvoices({ clientType, clientId, reservationId })
//      → array of invoices (open balance > $0.01) annotated with
//        `reservation_attribution: { [reservationId]: portion }` and `due`
//        (= total − amount_paid − amount_credited, see invoiceDue).
//        When `reservationId` is passed, the result is scoped to invoices that
//        are attributed to that reservation — used by the per-reservation
//        AddPayment flow so a payment recorded against one reservation can
//        never be auto-allocated onto a sibling reservation's invoice.
//
//   distributePaymentToReservations(attribution, amount)
//      → { [resId]: portionAmount } proportional to attribution shares.
//        (Retained for callers that need a UI preview of how a payment will
//        split across reservations; the actual ledger update is owned by
//        DB triggers — see the migration that creates
//        recompute_reservation_payments.)
//
//   applyAllocations({ transactionId, transactionAmount, allocations,
//                      createdBy })
//      → writes invoice_payment_allocations rows, updates each invoice's
//        amount_paid + status, and updates the transaction's
//        unallocated_amount + invoiced flag. reservations.paid / balance /
//        fully_paid are recomputed automatically by DB triggers, so this
//        helper no longer touches the reservations table.

import supabase from "../supabase-client";
import { alegraRequest, getAlegraRegime, invoiceDue } from "./invoicing";

// Inbound payments of $1.00 or less are bank penny-drops ("Pago para cotejar
// beneficiario") sent to verify our CLABE before the real transfer. They stay in
// `payments` (the money is in the bank) but are never listed as "por aplicar".
// The same goes for a leftover of $1.00 or less after applying (a client who
// sent $239.50 for $239): it stays as saldo a favor but is not chased.
export const MIN_LISTED_PAYMENT_CENTS = 101;

// Folios named in the transfer's concepto. Companies on weekly billing write
// them there — "F4700 F4702 Y F4703 FLETE LOCAL MTY" — and matching them saves
// hunting through twenty open invoices for the three that were actually paid.
// Three digits minimum, so "renta 2 dias" matches nothing.
export function foliosInConcept(concept) {
  return [...String(concept || "").matchAll(/\d{3,}/g)].map((m) => m[0]);
}
export function invoiceMatchesConcept(invoiceNumber, folios) {
  const n = String(invoiceNumber || "");
  return folios.some((f) => n.includes(f));
}

// Map our `accounts.id` to Alegra's bank account + forma de pago. Used when
// registering a payment / stamping a complemento de pago in Alegra.
export const ACCOUNT_TO_ALEGRA = {
  1: { bankAccount: 1, paymentMethod: "cash" },       // Efectivo → Caja chica
  2: { bankAccount: 6, paymentMethod: "transfer" },   // Afirme → AFIRME
  3: { bankAccount: 5, paymentMethod: "transfer" },   // Base → BASE
  4: { bankAccount: 7, paymentMethod: "credit-card" },// Clip → American Express
  5: { bankAccount: 6, paymentMethod: "credit-card" },// Terminal Afirme → AFIRME
  7: { bankAccount: 8, paymentMethod: "transfer" },   // Fintoc → FINTOC
};
// Account 6 ("Crédito de cliente") is deliberately absent: it is not real money
// arriving, so it never syncs to Alegra. applyAllocations detects it by the
// account TYPE and skips the sync entirely.

// Forma de pago for a transaction, card-aware.
//
// ACCOUNT_TO_ALEGRA maps an account to ONE forma de pago, which is right for a
// bank or for cash but not for a card terminal: SAT distinguishes 04 (crédito)
// from 28 (débito), and about a third of card volume is debit. The account only
// says "a card was used"; `card_type` says which kind, so it wins when known.
//
// This matters on a CFDI, which cannot be corrected once timbrado.
export function alegraPaymentForm(txn) {
  const base = ACCOUNT_TO_ALEGRA[txn?.account] || null;
  if (!base) return null;
  if (base.paymentMethod === "credit-card" && txn?.card_type === "debit") {
    return "debit-card";
  }
  return base.paymentMethod;
}

// Resolve the Alegra contact id + regime enum for an invoice's client — both
// are required to stamp a complemento de pago (REP).
async function resolveAlegraClient(invoice) {
  const isCompany = !!invoice.company_id;
  const table = isCompany ? "companies" : "customers";
  const id = isCompany ? invoice.company_id : invoice.customer_id;
  let contactId = invoice.alegra_contact_id || null;
  let regimeClient = null;
  if (id) {
    const { data } = await supabase
      .from(table)
      .select("rfc, regimen_fiscal, alegra_contact_id")
      .eq("id", id)
      .maybeSingle();
    if (data) {
      contactId = contactId || data.alegra_contact_id;
      regimeClient = getAlegraRegime(data.regimen_fiscal, data.rfc);
    }
  }
  return { contactId, regimeClient };
}

// ---------- Open invoices for a client ----------

export async function fetchClientOpenInvoices({
  clientType,
  clientId,
  reservationId = null,
  // Company payers only: every razón social the payment may cover (itself plus
  // the companies that use its CLABE). Defaults to just clientId.
  companyIds = null,
}) {
  if (!clientId) return [];
  let query = supabase
    .from("invoices_issued")
    .select("*")
    .gt("total", 0)
    .neq("status", "void")
    .order("date", { ascending: true });
  if (clientType === "company") query = query.in("company_id", companyIds?.length ? companyIds : [clientId]);
  else query = query.eq("customer_id", clientId);

  const { data, error } = await query;
  if (error) {
    console.error("fetchClientOpenInvoices:", error.message);
    return [];
  }

  const open = (data || []).filter(
    (inv) => invoiceDue(inv) > 0.01
  );
  if (open.length === 0) return [];

  // Pull invoice_reservations rows for any of these invoices in one query
  const ids = open.map((i) => i.id);
  const { data: joinRows } = await supabase
    .from("invoice_reservations")
    .select("invoice_id, reservation_id, quantity, unit_price, discount")
    .in("invoice_id", ids);

  const byInvoice = new Map();
  for (const j of joinRows || []) {
    const portion =
      (j.quantity || 0) * (j.unit_price || 0) -
      (j.quantity || 0) * (j.discount || 0);
    if (!byInvoice.has(j.invoice_id)) byInvoice.set(j.invoice_id, {});
    const map = byInvoice.get(j.invoice_id);
    map[j.reservation_id] = (map[j.reservation_id] || 0) + portion;
  }

  const annotated = open.map((inv) => {
    let attribution = byInvoice.get(inv.id) || null;
    // Single-res invoice: the reservation_id field carries the full total.
    if (!attribution && inv.reservation_id != null) {
      attribution = { [inv.reservation_id]: inv.total };
    }
    return {
      ...inv,
      due: invoiceDue(inv),
      reservation_attribution: attribution || {},
    };
  });

  // Strict reservation scoping: keep only invoices this reservation is part of.
  // Attribution keys come back as object-string keys, so compare loosely.
  if (reservationId != null) {
    const target = String(reservationId);
    return annotated.filter((inv) =>
      Object.keys(inv.reservation_attribution || {}).includes(target)
    );
  }

  return annotated;
}

// ---------- Distribute a single allocation across an invoice's reservations ----------

export function distributePaymentToReservations(attribution, amount) {
  const result = {};
  if (!attribution || amount <= 0) return result;
  const entries = Object.entries(attribution);
  const total = entries.reduce((s, [, v]) => s + (v || 0), 0);
  if (entries.length === 1 || total <= 0) {
    // Trivial: single reservation OR zero attribution → assign all to first id
    const targetId = entries[0]?.[0];
    if (targetId) result[targetId] = amount;
    return result;
  }
  // Proportional distribution. Last reservation absorbs rounding to keep
  // sum exactly equal to the input amount.
  let assigned = 0;
  entries.forEach(([resId, portion], idx) => {
    if (idx === entries.length - 1) {
      result[resId] = Math.round((amount - assigned) * 100) / 100;
    } else {
      const share = Math.round(((portion / total) * amount) * 100) / 100;
      result[resId] = share;
      assigned += share;
    }
  });
  return result;
}

// ---------- Available (unallocated) payment on a reservation ----------
//
// Sum of unallocated income (non-deposit) payments on the given reservation(s),
// newest first. Drives the pay-first → PUE decision at invoice time.

export async function getReservationUnallocated(reservationIds) {
  const ids = (Array.isArray(reservationIds) ? reservationIds : [reservationIds])
    .filter((x) => x != null);
  if (ids.length === 0) return { total: 0, transactions: [] };
  const { data, error } = await supabase
    .from("transactions")
    .select("id, amount, unallocated_amount, account, payment_date, created_at, card_type")
    .in("reservation", ids)
    .eq("type", "income")
    .eq("isDeposit", false)
    .gt("unallocated_amount", 0)
    .order("created_at", { ascending: false });
  if (error) {
    console.error("getReservationUnallocated:", error.message);
    return { total: 0, transactions: [] };
  }
  const rows = data || [];
  const total = rows.reduce((s, r) => s + (r.unallocated_amount || 0), 0);
  return { total: Math.round(total * 100) / 100, transactions: rows };
}

// Forma de pago (Alegra value) implied by a reservation's available payment —
// used to set a PUE invoice's forma de pago to the real method.
export async function getReservationPaymentForm(reservationIds) {
  const { transactions } = await getReservationUnallocated(reservationIds);
  const acc = transactions[0]?.account;
  return ACCOUNT_TO_ALEGRA[acc]?.paymentMethod || null;
}

// Allocate a reservation's available unallocated payments onto a just-created
// invoice, up to its outstanding total. Used for pay-first → PUE invoices so the
// CFDI is linked to its payment immediately (PUE ⇒ no complemento). Returns the
// amount allocated.
export async function allocateAvailableToInvoice({ invoice, reservationIds, createdBy }) {
  const { transactions } = await getReservationUnallocated(reservationIds);
  let remaining = invoiceDue(invoice);
  const working = { ...invoice };
  for (const t of transactions) {
    if (remaining <= 0.01) break;
    const amt = Math.round(Math.min(remaining, t.unallocated_amount) * 100) / 100;
    if (amt <= 0.01) continue;
    await applyAllocations({
      transactionId: t.id,
      transactionAmount: t.amount,
      allocations: [{ invoice: working, amount: amt }],
      createdBy,
    });
    working.amount_paid = (working.amount_paid || 0) + amt;
    remaining = Math.round((remaining - amt) * 100) / 100;
  }
  return Math.round((invoiceDue(invoice) - remaining) * 100) / 100;
}

// ---------- Apply allocations atomically (best-effort) ----------
//
// allocations: [{ invoice, amount }] — `invoice` should be the row from
//   fetchClientOpenInvoices (carries reservation_attribution).
// transactionAmount: the gross amount on the transaction header — used to
//   compute the transaction's trailing unallocated_amount.

// Undo the DB allocation writes from a failed call (newest first).
async function rollbackAllocations(written) {
  for (const w of written) {
    await supabase.from("invoice_payment_allocations").delete().eq("id", w.allocId);
    await supabase
      .from("invoices_issued")
      .update({ amount_paid: w.prevPaid, status: w.prevStatus })
      .eq("id", w.invoice.id);
  }
}

// Alegra rejects a payment bigger than what the invoice still owes, and its
// balance can disagree with ours (migrated invoices, aggregate CFDI rounding).
// Checking every target BEFORE the first POST is what keeps a rejection from
// stranding an already-stamped complemento — see the ordering note below.
// Runs in parallel: the proxy costs seconds per call.
async function assertAlegraBalances(rows) {
  if (rows.length === 0) return;
  const checked = await Promise.all(
    rows.map(async (w) => {
      try {
        const inv = await alegraRequest(
          `invoices/${w.invoice.alegra_id}?fields=balance`,
          "GET"
        );
        return { w, balance: inv?.balance == null ? null : Number(inv.balance) };
      } catch {
        // Unreadable → let Alegra itself be the judge on the POST.
        return { w, balance: null };
      }
    })
  );
  const bad = checked.find(
    (c) => c.balance != null && c.w.amount > c.balance + 0.01
  );
  if (!bad) return;
  const label =
    bad.w.invoice.invoice_number || `#${bad.w.invoice.alegra_id}`;
  throw new Error(
    `Alegra reporta un saldo de $${bad.balance} en la factura ${label}, ` +
      `menor a los $${bad.w.amount} que intentas aplicar. ` +
      `Ajusta la distribución del pago.`
  );
}

// A stamped complemento (REP) cannot be un-stamped, so a failed POST is never
// proof that nothing was created — the response can be lost after Alegra
// already timbró. Go look for the payment before rolling anything back.
// Matches date + total + the exact invoice set, requires a stamp, and skips any
// payment our DB already references so a legitimate duplicate payment on the
// same day is never adopted twice.
async function findStampedPayment({ date, invoices }) {
  const expectedCents = Math.round(
    invoices.reduce((s, i) => s + (Number(i.amount) || 0), 0) * 100
  );
  const ids = new Set(invoices.map((i) => String(i.id)));
  let list;
  try {
    list = await alegraRequest("payments?order_direction=DESC&limit=30", "GET");
  } catch {
    return null;
  }
  const candidates = (Array.isArray(list) ? list : []).filter(
    (p) =>
      p?.stamp?.uuid &&
      p.date === date &&
      Math.round((Number(p.amount) || 0) * 100) === expectedCents &&
      (p.invoices || []).length === ids.size &&
      (p.invoices || []).every((i) => ids.has(String(i.id)))
  );
  for (const c of candidates) {
    const { data } = await supabase
      .from("invoice_payment_allocations")
      .select("id")
      .eq("alegra_payment_id", String(c.id))
      .limit(1);
    if (!data || data.length === 0) return c;
  }
  return null;
}

export async function applyAllocations({
  transactionId,
  transactionAmount,
  allocations,
  createdBy,
}) {
  // Pull a reliable account + date for the Alegra sync. Some callers embed
  // `account(name)` which clobbers the scalar id, so read it straight from DB.
  const { data: txn } = await supabase
    .from("transactions")
    .select("account, payment_date, amount, card_type")
    .eq("id", transactionId)
    .maybeSingle();
  // No silent default here. An unmapped account used to fall back to
  // { bankAccount: 1, paymentMethod: "cash" }, which meant a Fintoc SPEI would
  // be stamped on the complemento as efectivo into Caja chica — the wrong bank
  // AND the wrong forma de pago (SAT 01 instead of 03) on a CFDI that cannot be
  // corrected once timbrado. An unknown account must stop the process, loudly.
  const alegraMap = ACCOUNT_TO_ALEGRA[txn?.account] || null;
  const payDate = txn?.payment_date || new Date().toLocaleDateString("en-CA");

  // Internal "Crédito de cliente" (account type 'credit') is not real money
  // entering — it's already-received money being reassigned from the profile
  // ledger to this invoice. It must reduce the invoice balance but must NOT
  // emit a complemento de pago (the REP, if owed, was stamped when the original
  // overpayment came in). Detect it so we can skip the Alegra sync entirely.
  let isInternalCredit = false;
  if (txn?.account) {
    const { data: acct } = await supabase
      .from("transaction_accounts")
      .select("type")
      .eq("id", txn.account)
      .maybeSingle();
    isInternalCredit = acct?.type === "credit";
  }

  // 1. DB writes first (allocation rows + invoice amount_paid/status). Track
  //    them so we can roll the whole batch back if the Alegra sync fails.
  const written = []; // { allocId, invoice, amount, prevPaid, prevStatus }
  let totalAllocated = 0;
  for (const { invoice, amount } of allocations) {
    if (!invoice || !amount || amount <= 0) continue;
    const { data: allocRow, error: allocError } = await supabase
      .from("invoice_payment_allocations")
      .insert({ transaction_id: transactionId, invoice_id: invoice.id, amount, created_by: createdBy || null })
      .select("id")
      .single();
    if (allocError) {
      await rollbackAllocations(written);
      throw new Error(allocError.message);
    }
    const prevPaid = invoice.amount_paid || 0;
    const prevStatus = invoice.status;
    const newAmountPaid = Math.round((prevPaid + amount) * 100) / 100;
    const isClosed = newAmountPaid + (invoice.amount_credited || 0) + 0.01 >= invoice.total;
    const { error: invError } = await supabase
      .from("invoices_issued")
      .update({ amount_paid: newAmountPaid, ...(isClosed ? { status: "closed" } : {}) })
      .eq("id", invoice.id);
    if (invError) {
      await supabase.from("invoice_payment_allocations").delete().eq("id", allocRow.id);
      await rollbackAllocations(written);
      throw new Error(invError.message);
    }
    written.push({ allocId: allocRow.id, invoice, amount, prevPaid, prevStatus });
    totalAllocated += amount;
  }

  // 2. Alegra sync — ONE payment per type so a payment across many invoices
  //    produces a SINGLE complemento de pago (REP) listing every PPD invoice as
  //    a DoctoRelacionado, plus one plain payment for the PUE invoices.
  //    Unstamped/draft invoices (no alegra_id) are skipped.
  //
  //    These are TWO independent POSTs, and only one of them is reversible: a
  //    plain payment can be deleted from Alegra, a stamped complemento cannot
  //    (a timbrado CFDI is final). So the order below is load-bearing —
  //    validate, then plain, then stamped — and every failure path has to
  //    account for what already exists on Alegra's side. Getting this wrong
  //    strands a live REP against a payment our DB says never happened.
  //
  // Internal credit never syncs to Alegra — the DB writes above already
  // reduced the invoice balance; emitting a payment/REP here would double-count
  // money that already entered the books when the overpayment was received.
  const stamped = isInternalCredit
    ? []
    : written.filter((w) => w.invoice.alegra_id && w.invoice.payment_type === "PPD");
  const plain = isInternalCredit
    ? []
    : written.filter((w) => w.invoice.alegra_id && w.invoice.payment_type !== "PPD");
  const baseFor = (rows) => ({
    date: payDate,
    bankAccount: alegraMap.bankAccount,
    // 04 vs 28: a debit card is not a credit card on a CFDI.
    paymentMethod: alegraPaymentForm(txn),
    type: "in",
    invoices: rows.map((w) => ({ id: w.invoice.alegra_id, amount: w.amount })),
  });

  // 2a. Validate every target against Alegra's own balances first. Nothing has
  //     been sent yet, so a rejection here costs nothing.
  try {
    // One complemento has ONE receptor, resolved from the first invoice below.
    // Razones sociales sharing a CLABE (companies.paid_by_company_id) make a
    // mixed batch possible; stamping it would put the wrong RFC on a CFDI.
    const receptor = (inv) => (inv.company_id ? `c${inv.company_id}` : `p${inv.customer_id}`);
    if (new Set(stamped.map((w) => receptor(w.invoice))).size > 1) {
      throw new Error("Aplica las facturas de una razón social a la vez: cada complemento va a nombre de una sola empresa.");
    }
    if ((plain.length || stamped.length) && !alegraMap) {
      throw new Error(
        `La cuenta ${txn?.account} no tiene cuenta bancaria de Alegra configurada; ` +
          "no se puede registrar el pago sin saber con qué forma de pago timbrarlo."
      );
    }
    await assertAlegraBalances([...plain, ...stamped]);
  } catch (e) {
    await rollbackAllocations(written);
    throw e;
  }

  // 2b. Reversible half first.
  let plainPaymentId = null;
  // True when the POST reported a failure but the complemento had in fact been
  // stamped, so we adopted it. The caller should say so: from the outside this
  // looks like an error followed by success, and silence invites someone to
  // "fix" a CFDI that is already correct.
  let adoptedStamp = false;
  try {
    if (plain.length) {
      const resp = await alegraRequest("payments", "POST", baseFor(plain));
      plainPaymentId = resp?.id ? String(resp.id) : null;
      await supabase
        .from("invoice_payment_allocations")
        .update({ alegra_payment_id: plainPaymentId })
        .in("id", plain.map((w) => w.allocId));
    }
    // 2c. Irreversible half last.
    if (stamped.length) {
      // All invoices in one payment belong to the same payer → resolve once.
      const { contactId, regimeClient } = await resolveAlegraClient(stamped[0].invoice);
      if (!contactId || !regimeClient) {
        throw new Error("Falta el contacto o régimen fiscal del cliente para timbrar el complemento.");
      }
      const payload = {
        ...baseFor(stamped),
        client: { id: contactId },
        regimeClient,
        stamp: { generateStamp: true, version: "4.0" },
      };
      let resp;
      try {
        resp = await alegraRequest("payments", "POST", payload);
      } catch (e) {
        // The stamp may have gone through even though the response didn't come
        // back. Adopt it if it exists — rolling back would orphan a live REP.
        resp = await findStampedPayment(payload);
        if (!resp) throw e;
        adoptedStamp = true;
      }
      await supabase
        .from("invoice_payment_allocations")
        .update({
          alegra_payment_id: resp?.id ? String(resp.id) : null,
          rep_uuid: resp?.stamp?.uuid || null,
          rep_stamped_at: resp?.stamp?.stampDate || new Date().toISOString(),
        })
        .in("id", stamped.map((w) => w.allocId));
    }
  } catch (e) {
    // Undo the reversible half so Alegra isn't left holding a payment for
    // allocations we're about to delete.
    if (plainPaymentId) {
      try {
        await alegraRequest(`payments/${plainPaymentId}`, "DELETE");
      } catch {}
    }
    await rollbackAllocations(written);
    throw new Error("No se pudo registrar el pago / complemento en Alegra: " + (e?.message || e));
  }

  // 3. Update the transaction's residual unallocated + invoiced flag.
  //    Derive it from the CUMULATIVE allocations on this transaction (read back
  //    from the DB), NOT just this call's `totalAllocated`. A payment allocated
  //    across invoices in SEPARATE calls (e.g. AddPayment then a follow-up)
  //    would otherwise overwrite unallocated with `amount − thisCallOnly`,
  //    leaving a stale residual that the reservation recompute double-counts as
  //    a phantom credit (negative balance / fake overpayment).
  const { data: allAllocs } = await supabase
    .from("invoice_payment_allocations")
    .select("amount")
    .eq("transaction_id", transactionId);
  const cumulativeAllocated = (allAllocs || []).reduce(
    (s, r) => s + (Number(r.amount) || 0),
    0
  );
  // Excess already moved to the client's credit ledger left this payment for
  // good; counting it as unallocated again resurrects it as a second
  // "excedente" that can be moved to credit twice (res 1443 / company 97).
  const { data: movedToCredit } = await supabase
    .from("client_credit_ledger")
    .select("amount")
    .eq("source_transaction_id", transactionId)
    .eq("reason", "overpayment");
  const creditMoved = (movedToCredit || []).reduce(
    (s, r) => s + (Number(r.amount) || 0),
    0
  );
  const txnAmount = txn?.amount ?? transactionAmount ?? 0;
  const newUnallocated = Math.max(
    0,
    Math.round((txnAmount - cumulativeAllocated - creditMoved) * 100) / 100
  );
  const { error: txError } = await supabase
    .from("transactions")
    .update({
      unallocated_amount: newUnallocated,
      invoiced: cumulativeAllocated > 0,
    })
    .eq("id", transactionId);
  if (txError) throw new Error(txError.message);

  return { totalAllocated, unallocated: newUnallocated, adoptedStamp };
}
